import api from './client';
import type { PowerData } from './dashboard';

export interface DetailCards {
  plantId: number;
  plantName: string;
  instantPowerKw?: number | null;
  dailyTotalPower: string;
  weeklyTotalPower: string;
  monthlyTotalPower: string;
  yearlyTotalPower: string;
  lifetimeTotalPower: string;
  invertersCount: number;
  activeInvertersCount: number;
  alarmTotalCount: number;
  alarmWarningCount: number;
  alarmMinorCount: number;
  alarmMajorCount: number;
  taskTotalCount: number;
  taskTodoCount: number;
  taskInProgressCount: number;
  taskDoneCount: number;
  taskCanceledCount: number;
  plantCity: string;
  plantTown: string;
  plantLatitude: number;
  plantLongitude: number;
  sensorsCount: number;
  activeSensorsCount: number;
  smartloggerDeviceCount: number;
  meterCount: number;
}

export interface InverterPowerRow {
  date_name: string;
  inverter_name: string;
  power_total: number;
  date_year?: number;
  date_month?: number;
  date_day?: number;
  date_hour?: number;
}

export interface PerformanceRow {
  pp_log_date: string;
  pp_power_name: string;
  pp_power_value: number;
}

export interface DetailCharts {
  hourly: PowerData[];
  daily: PowerData[];
  monthly: PowerData[];
  yearly: PowerData[];
  inverterHourly: InverterPowerRow[];
  inverterDaily: InverterPowerRow[];
  inverterMonthly: InverterPowerRow[];
  inverterYearly: InverterPowerRow[];
  performance: PerformanceRow[];
}

export interface PvlibExpectedPoint {
  timestamp: string;
  expectedKwh: number;
}

export async function fetchPvlibExpectedHourly(uuid_id: string, signal?: AbortSignal): Promise<PvlibExpectedPoint[]> {
  const r = await api.get<{ series: PvlibExpectedPoint[] }>(`/powerplants/${uuid_id}/pvlib-expected-hourly`, { signal });
  return r.data.series ?? [];
}

export async function fetchPvlibExpectedRange(uuid_id: string): Promise<PvlibExpectedPoint[]> {
  const r = await api.get<{ series: PvlibExpectedPoint[] }>(`/powerplants/${uuid_id}/pvlib-expected-range`, {
    params: { range: 'true' },
  });
  return r.data.series ?? [];
}

export async function fetchOpenMeteoExpectedHourly(uuid_id: string, signal?: AbortSignal): Promise<PvlibExpectedPoint[]> {
  const r = await api.get<{ series: PvlibExpectedPoint[] }>(`/powerplants/${uuid_id}/open-meteo-expected-hourly`, { signal });
  return r.data.series ?? [];
}

export async function fetchDetailCards(uuid_id: string, signal?: AbortSignal): Promise<DetailCards> {
  const r = await api.get<DetailCards>(`/powerplants/${uuid_id}/cards`, { signal });
  return r.data;
}

export async function fetchDetailCharts(uuid_id: string, signal?: AbortSignal): Promise<DetailCharts> {
  const r = await api.get<DetailCharts>(`/powerplants/${uuid_id}/charts`, { signal });
  return r.data;
}

export async function fetchPlantPowerDrillChart(
  uuid_id: string,
  year: number,
  month: number | null,
  day: number | null,
): Promise<PowerData[]> {
  const params: Record<string, string> = { year: String(year) };
  if (month !== null) params.month = String(month);
  if (day !== null) params.day = String(day);
  const r = await api.get<{ data: PowerData[] }>(`/powerplants/${uuid_id}/power-chart/drill`, { params });
  return r.data.data;
}

export async function fetchInverterLineDrill(
  uuid_id: string,
  year: number,
  month: number | null,
  day: number | null,
): Promise<InverterPowerRow[]> {
  const params: Record<string, string> = { year: String(year) };
  if (month !== null) params.month = String(month);
  if (day !== null) params.day = String(day);
  const r = await api.get<{ data: InverterPowerRow[] }>(`/powerplants/${uuid_id}/charts/drill`, { params });
  return r.data.data;
}
